/**
 * Lista de playbooks (visão default ao entrar na tela).
 *
 * Colunas com fonte real no serializer: Nome (+ versão) · Status · Vertical ·
 * Etapas (contagem defensiva). A coluna "Conv. usando" do mockup é omitida —
 * não há campo de contagem de conversas no backend (zero dado falso).
 *
 * PR5 — ciclo de vida (gate `playbook-author`): "Novo playbook" (form mínimo →
 * editor do novo id), "Duplicar" (cópia client-side → editor) e "Arquivar"
 * (soft-delete → `deprecated`, com confirmação não-destrutiva + If-Match). As
 * ações são defense-in-depth: só renderizam com a capability E o backend as
 * gateia; sem `playbook-author` a lista é idêntica ao PR2 (somente leitura).
 */

import { Modal } from '@lume/ui-components';
import { useState } from 'react';

import { useCapability } from '../../hooks/use-capability';
import { ApiError } from '../../lib/api-client';

import { buildDuplicatePayload } from './playbook-create';
import { countSteps, verticalLabel } from './playbook-labels';
import { PlaybookCreateModal } from './PlaybookCreateModal';
import { PlaybookStatusPill } from './PlaybookStatusPill';
import { useArchivePlaybook, useCreatePlaybook } from './use-playbooks';

import type { PlaybookDto } from './types';

interface Feedback {
  kind: 'success' | 'error';
  text: string;
}

export function PlaybookListView({
  playbooks,
  onOpen,
}: {
  playbooks: PlaybookDto[];
  onOpen: (id: string) => void;
}): JSX.Element {
  const canAuthor = useCapability('playbook-author');
  const [showCreate, setShowCreate] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<PlaybookDto | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const duplicateMutation = useCreatePlaybook();
  const archiveMutation = useArchivePlaybook();

  const handleCreated = (created: PlaybookDto): void => {
    setShowCreate(false);
    setFeedback(null);
    onOpen(created.id);
  };

  const handleDuplicate = (pb: PlaybookDto): void => {
    if (duplicateMutation.isPending) return;
    setFeedback(null);
    duplicateMutation.mutate(buildDuplicatePayload(pb), {
      onSuccess: (created) => {
        onOpen(created.id);
      },
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409) {
          setFeedback({
            kind: 'error',
            text: `Já existe uma cópia de “${pb.name}”. Renomeie a cópia anterior antes de duplicar de novo.`,
          });
          return;
        }
        setFeedback({
          kind: 'error',
          text: 'Não foi possível duplicar o playbook. Tente novamente.',
        });
      },
    });
  };

  const handleConfirmArchive = (): void => {
    if (archiveTarget === null || archiveMutation.isPending) return;
    const target = archiveTarget;
    setFeedback(null);
    archiveMutation.mutate(
      { id: target.id, expectedVersion: target.version },
      {
        onSuccess: () => {
          setArchiveTarget(null);
          setFeedback({ kind: 'success', text: `“${target.name}” foi arquivado.` });
        },
        onError: (err) => {
          setArchiveTarget(null);
          if (err instanceof ApiError && err.status === 409) {
            setFeedback({
              kind: 'error',
              text: `“${target.name}” mudou desde que a lista foi carregada. Ela foi atualizada — tente arquivar novamente.`,
            });
            return;
          }
          setFeedback({
            kind: 'error',
            text: 'Não foi possível arquivar o playbook. Tente novamente.',
          });
        },
      },
    );
  };

  const isEmpty = playbooks.length === 0;

  return (
    <div className="playbooks-screen">
      <div className="pb-pagehead">
        <div className="pb-pagehead__heading">
          <div className="pb-pagehead__title">
            <span className="pb-pagehead__name">Playbooks</span>
          </div>
          {!isEmpty && (
            <div className="pb-pagehead__sub">
              {`${playbooks.length.toString()} ${
                playbooks.length === 1 ? 'playbook' : 'playbooks'
              }${canAuthor ? '' : ' · somente leitura'}`}
            </div>
          )}
        </div>
        {canAuthor && (
          <div className="pb-pagehead__actions">
            <button
              type="button"
              className="pb-btn pb-btn--primary"
              onClick={() => {
                setShowCreate(true);
              }}
            >
              + Novo playbook
            </button>
          </div>
        )}
      </div>

      {feedback !== null && (
        <div
          className={'pb-feedback pb-feedback--' + feedback.kind}
          role={feedback.kind === 'error' ? 'alert' : 'status'}
        >
          {feedback.text}
        </div>
      )}

      <div className="pb-editor-layout pb-editor-layout--no-side">
        <div className="pb-editor-main">
          {isEmpty ? (
            <div className="pb-panel pb-empty">
              <h2 className="pb-panel__title">Nenhum playbook neste tenant</h2>
              <p className="pb-panel__subtitle">
                {canAuthor
                  ? 'Crie o primeiro playbook para começar.'
                  : 'Quando playbooks forem criados, eles aparecerão aqui.'}
              </p>
            </div>
          ) : (
            <table className="pb-listtable">
              <thead className="pb-listtable__head">
                <tr>
                  <th>Nome</th>
                  <th>Status</th>
                  <th>Vertical</th>
                  <th className="pb-col-right">Etapas</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {playbooks.map((pb) => (
                  <tr key={pb.id} className="pb-listtable__row">
                    <td>
                      <div className="pb-listtable__name">{pb.name}</div>
                      <div className="pb-listtable__meta">v{pb.version}</div>
                    </td>
                    <td>
                      <PlaybookStatusPill status={pb.status} />
                    </td>
                    <td>{verticalLabel(pb.vertical)}</td>
                    <td className="pb-col-right">{countSteps(pb.steps)}</td>
                    <td>
                      <div className="pb-listtable__actions">
                        <button
                          type="button"
                          className="pb-btn pb-btn--sm"
                          onClick={() => onOpen(pb.id)}
                        >
                          Abrir
                        </button>
                        {canAuthor && (
                          <>
                            <button
                              type="button"
                              className="pb-btn pb-btn--sm pb-btn--ghost"
                              disabled={duplicateMutation.isPending}
                              onClick={() => handleDuplicate(pb)}
                            >
                              Duplicar
                            </button>
                            {pb.status !== 'deprecated' && (
                              <button
                                type="button"
                                className="pb-btn pb-btn--sm pb-btn--ghost"
                                onClick={() => setArchiveTarget(pb)}
                              >
                                Arquivar
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {canAuthor && (
        <PlaybookCreateModal
          isOpen={showCreate}
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}

      {canAuthor && (
        <Modal
          isOpen={archiveTarget !== null}
          onClose={() => setArchiveTarget(null)}
          title="Arquivar playbook"
          confirmLabel={archiveMutation.isPending ? 'Arquivando…' : 'Arquivar'}
          cancelLabel="Cancelar"
          onConfirm={handleConfirmArchive}
        >
          <p className="pb-modal-text">
            {archiveTarget !== null && (
              <>
                “{archiveTarget.name}” será marcado como <strong>Descontinuado</strong> e sairá de
                uso. Conversas em curso mantêm a versão atual. Esta ação é reversível — o playbook
                continua na lista e pode ser duplicado.
              </>
            )}
          </p>
        </Modal>
      )}
    </div>
  );
}
